import { useCallback, useEffect, useState } from 'react';
import { ApiError, getStorageSummary, type StorageSummary } from '@/lib/api';

/**
 * The Storage surface's allowance-versus-usage figures (STO-1..STO-3, STO-6).
 *
 * The same shape as `useUsers` and `useSpaces`.
 *
 * **Separate from `useStorageUsage`, which is the sibling this hook exists
 * beside rather than inside.** The summary is what an admin came for — how much
 * room is left — and the series is context. One hook fetching both would blank
 * the figures whenever the chart failed, which is `FolderView`'s rule about its
 * supplementary `useFolders` call, one surface over.
 *
 * **This is not authorization.** SEC-5 puts that in the API, which refuses a
 * non-admin caller and a plan-less agency (STO-5) whatever this hook returns.
 */

export type StorageSummaryState =
  | { kind: 'loading' }
  /**
   * An agency holding nothing is a loaded state, not an empty one: a measured
   * zero is what lets the allowance meter render at all.
   */
  | { kind: 'loaded'; summary: StorageSummary }
  /**
   * `status` carried for the reason every hook here carries it: a 403 is
   * permanent and offers no retry, a 500 is worth one. `0` is network-level.
   *
   * A 403 here has **two** causes the surface must tell apart — a non-admin, and
   * an agency with no plan (STO-5) — and the API's own message is what
   * distinguishes them, which is why the surface reaches for `messageFrom`.
   */
  | { kind: 'error'; status: number; error: unknown };

export interface UseStorageSummaryResult {
  state: StorageSummaryState;
  /** After a failure. Clears back to `loading` first. */
  retry: () => void;
}

export function useStorageSummary(): UseStorageSummaryResult {
  const [state, setState] = useState<StorageSummaryState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getStorageSummary(controller.signal)
      .then((summary) => {
        setState({ kind: 'loaded', summary });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({
          kind: 'error',
          status: error instanceof ApiError ? error.status : 0,
          error,
        });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry };
}
